import React, {useCallback, useEffect, useMemo} from 'react';
import {useSearchParams} from 'react-router-dom';
import {Modal} from '@lightricks/react-design-system';
import raiseFlashMessage, {
  raiseFlashMessageError,
} from '@/utils/raiseFlashMessage';
import translate from '@/utils/translate';
import {ContentSubmissionVersion} from '@/types/contentSubmission';
import useUpdateContentSubmissionAnalysis from '@/hooks/mutations/use-update-content-submission-analysis';
import useContentSubmissionVersionQuery from '@/hooks/queries/use-content-submission-version-query';
import useToken from '@/hooks/use-token';
import styles from './ContentSubmissionAnalysisModal.module.scss';
import AnalysisContent from './components/AnalysisContent';
import AnalysisRatingForm from './components/AnalysisRatingForm';

function ContentSubmissionAnalysisModal() {
  const {isAuthenticated} = useToken(true, true);
  const [searchParams, setSearchParams] = useSearchParams();
  const contentSubmissionVersionId = searchParams.get(
    'contentSubmissionAnalysis'
  );

  const isOpen = !!contentSubmissionVersionId && isAuthenticated;

  const {
    data: contentSubmissionVersion,
    isLoading,
    isError,
  } = useContentSubmissionVersionQuery({
    contentSubmissionVersionId: contentSubmissionVersionId || undefined,
    enabled: isOpen,
  }) as {
    data: ContentSubmissionVersion | undefined;
    isLoading: boolean;
    isError: boolean;
  };

  const handleClose = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('contentSubmissionAnalysis');
      return next;
    });
  }, [setSearchParams]);

  useEffect(() => {
    if (!isAuthenticated && contentSubmissionVersionId) {
      handleClose();
    }
  }, [contentSubmissionVersionId, isAuthenticated, handleClose]);

  useEffect(() => {
    if (isError) {
      raiseFlashMessageError();
      handleClose();
    }
  }, [isError, handleClose]);

  const contentSubmissionAnalysis =
    contentSubmissionVersion?.contentSubmissionAnalysis ?? null;

  const contentSubmissionForDisplay = useMemo(() => {
    if (!contentSubmissionVersion) {
      return null;
    }

    const nested = contentSubmissionVersion.contentSubmission;

    return {
      id: contentSubmissionVersion.id,
      kind: contentSubmissionVersion.kind ?? nested?.kind,
      url: contentSubmissionVersion.url,
      thumbnailUrl: contentSubmissionVersion.thumbnailUrl,
      profile: nested?.profile ?? contentSubmissionVersion.profile,
      campaign: nested?.campaign ?? undefined,
    };
  }, [contentSubmissionVersion]);

  const updateAnalysis = useUpdateContentSubmissionAnalysis({
    contentSubmissionVersionId: contentSubmissionVersionId || undefined,
  });

  const handleSubmitRating = useCallback(
    (payload: {analysisRating: number | null; analysisNote: string | null}) => {
      const analysisId =
        contentSubmissionVersion?.contentSubmissionAnalysis?.id;
      if (!analysisId) {
        return;
      }
      updateAnalysis.mutate({
        id: analysisId,
        analysisRating: payload.analysisRating,
        analysisNote: payload.analysisNote,
      });
      raiseFlashMessage({
        status: 'success',
        mode: 'dark',
        message: translate('Rating and note updated successfully'),
        duration: 3000,
      });
    },
    [contentSubmissionVersion?.contentSubmissionAnalysis?.id, updateAnalysis]
  );

  useEffect(() => {
    if (updateAnalysis.isError) {
      raiseFlashMessageError();
    }
  }, [updateAnalysis.isError]);

  const analysis = contentSubmissionVersion?.contentSubmissionAnalysis;

  return (
    <Modal
      open={isOpen}
      showCloseButton={false}
      handleClose={handleClose}
      className={styles.modal}
    >
      <div className={styles.modalContent}>
        <AnalysisContent
          isLoading={isLoading}
          contentSubmissionAnalysis={contentSubmissionAnalysis}
          contentSubmission={contentSubmissionForDisplay}
        />
        {contentSubmissionAnalysis && !isLoading && (
          <AnalysisRatingForm
            initialRating={contentSubmissionAnalysis.analysisRating ?? null}
            initialNote={contentSubmissionAnalysis.analysisNote ?? null}
            onSubmit={handleSubmitRating}
            isSubmitting={updateAnalysis.isLoading}
          />
        )}
      </div>
    </Modal>
  );
}

export default ContentSubmissionAnalysisModal;
